import "./styles.css";
import "./app.css";
import Dashboard from "./components/dashboard/Dashboard";
import { DataProvider, useData } from "./contexts/DataContext";


function AppContent() {
  const { loading, error, loadingProgress, refetch } = useData();

  const principalLogos = [
    "/assets/logos/InnosuisseEN.png",
  ];

  const partnerLogos = [
    "/assets/logos/ubs_250.jpg",
    "/assets/logos/DeepTechNation_logo_RGB_gradient.png",
    "/assets/logos/grs_logo_CMYK.jpg",
  ];

  const contributorLogos = [
    "/assets/logos/switzerlandinnovationlogo.jpg",
    "/assets/logos/SEF-logo_combo_black_rgb.jpg",
    "/assets/logos/swissnex2025-500pixel.jpg",
    "/assets/logos/Innovaud250.jpg",
    "/assets/logos/swisscomlogo19_250.png",
    "/assets/logos/KendrislogoD250.jpg",
    "/assets/logos/venturelab_Logo_pos_rgb.png",
    "/assets/logos/StartupDays_23.jpg",
    "/assets/logos/IFJ_Logo_2018_neu220.png",
    "/assets/logos/FIT-logo-vert-250.png",
    "/assets/logos/UniversitätZurichLogo250.jpg",
    "/assets/logos/Lichtsteiner250.jpg",
  ];

  const defaultLogoUrl = "https://www.innosuisse.admin.ch/en/support-for-start-ups";

  const logoUrlMap: Record<string, string> = {
    "/assets/logos/grs_logo_CMYK.jpg": "https://www.grstiftung.ch/de.html",
    "/assets/logos/DeepTechNation_logo_RGB_gradient.png": "https://deeptechnation.ch/",
    "/assets/logos/ubs_250.jpg": "https://www.ubs.com/ch/en/services/mortgages-and-financing/special-financing/growth-advisory.html?bulkredirectlink=/ch/en/corporates/corporate-finance/growth-advisory.html",
    "/assets/logos/Innovaud250.jpg": "https://www.innovaud.ch/en/services/invest-in-the-canton-of-vaud/our-invest-services?utm_source=StartuptickerCH&utm_medium=Banner-23&utm_campaign=startupticker-partnership-2023",
    "/assets/logos/IFJ_Logo_2018_neu220.png": "https://www.ifj.ch/",
    "/assets/logos/venturelab_Logo_pos_rgb.png": "https://www.venturelab.swiss/",
    "/assets/logos/StartupDays_23.jpg": "https://startupdays.ch/",
    "/assets/logos/swisscomlogo19_250.png": "https://www.swisscom.ch/de/about.html",
    "/assets/logos/SEF-logo_combo_black_rgb.jpg": "https://www.swisseconomic.ch/de",
    "/assets/logos/UniversitätZurichLogo250.jpg": "https://www.uzh.ch/en.html",
    "/assets/logos/FIT-logo-vert-250.png": "https://www.fondation-fit.ch/en",
    "/assets/logos/KendrislogoD250.jpg": "https://www.kendris.com/en/services/business-advisory-and-outsourcing/",
    "/assets/logos/Lichtsteiner250.jpg": "https://lichtsteinerfoundation.org/",
    "/assets/logos/swissnex2025-500pixel.jpg": "https://swissnex.org/services/startups/",
    "/assets/logos/switzerlandinnovationlogo.jpg": "https://www.switzerland-innovation.com/",
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-container">
          <img
            src="/assets/icons/logo.png"
            alt="Swiss Startup Ecosystem Logo"
            className="loading-logo"
          />
          <div
            className="loading-spinner"
            style={{ borderColor: "var(--color-brand-primary)" }}
          ></div>
          <p className="loading-progress-text">{loadingProgress}</p>
          <div className="loading-progress-bar-container">
            <div
              className="loading-progress-bar"
              style={{
                backgroundColor: "var(--color-brand-primary)",
                width: "75%",
              }}
            ></div>
          </div>
          <p className="loading-description">Preparing the latest Swiss venture capital data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isDevelopment = process.env.NODE_ENV === "development";

    return (
      <div className="error-screen">
        <div className="error-container">
          <div className="error-icon-wrapper">
            <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h1 className="error-title">Unable to Load Data</h1>
          <p className="error-description">
            We're having trouble loading the Swiss Venture Capital Report data. This is a temporary
            issue and we're working to resolve it.
          </p>

          <button onClick={() => refetch()} className="error-retry-button">
            <svg className="error-retry-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Retry Loading
          </button>

          {isDevelopment && (
            <details className="error-details">
              <summary className="error-details-summary">
                🔧 Technical Details (Development Mode)
              </summary>
              <div className="error-details-content">
                <div className="error-section">
                  <strong className="error-section-title">Error Message:</strong>
                  <code className="error-message-code">{error}</code>
                </div>
                <div className="error-section">
                  <strong className="error-section-title">Developer Checklist:</strong>
                  <ul className="error-checklist">
                    <li className="error-checklist-item">
                      <span className="error-checklist-bullet">•</span>
                      <span>Backend server running at configured URL?</span>
                    </li>
                    <li className="error-checklist-item">
                      <span className="error-checklist-bullet">•</span>
                      <span>API endpoints accessible? (/api/v1/filters, /api/v1/deals)</span>
                    </li>
                    <li className="error-checklist-item">
                      <span className="error-checklist-bullet">•</span>
                      <span>Redis data synced? (run /sync endpoint if needed)</span>
                    </li>
                    <li className="error-checklist-item">
                      <span className="error-checklist-bullet">•</span>
                      <span>Check browser console for additional errors</span>
                    </li>
                    <li className="error-checklist-item">
                      <span className="error-checklist-bullet">•</span>
                      <span>Verify CORS configuration</span>
                    </li>
                  </ul>
                </div>
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="app-header">
        <div className="header-inner">
          <div className="header-left">
            <img src="/assets/icons/logo.png" alt="svcr logo" className="header-logo" />
            <div className="svcr-dashboard-title">
              <span className="header-title-main">Swiss Venture Capital Report</span>
              <span className="header-title-dashboard">Dashboard</span>
            </div>
          </div>
          <nav className="header-nav">
            <div className="header-nav-left-links">
              <a href="https://startupticker.ch/en/about" className="header-nav-link">About us</a>
              <a href="https://startupticker.ch/en/contact" className="header-nav-link">Contact</a>
              <a href="https://startupticker.ch/en/partners" className="header-nav-link">Partners</a>
            </div>
            <a href="https://startupticker.ch" className="header-back-link">back to startupticker.ch</a>
          </nav>
        </div>
      </header>

      <div className="app-layout">
        <div className="app app-container">
          <Dashboard />
        </div>

        <aside className="aside">
          <div className="aside-column">
            <div className="aside-section">
              <div className="aside-section-title">Principal</div>
              <div className="aside-logos-list">
                {principalLogos.map((src, i) => (
                  <div className="aside-sponsor-item" key={`p-${i}`}>
                    <a href={logoUrlMap[src] || defaultLogoUrl} target="_blank" rel="noopener noreferrer">
                      <img src={src} alt={`Principal ${i + 1}`} />
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="aside-section">
              <div className="aside-section-title">Partners</div>
              <div className="aside-logos-list">
                {partnerLogos.map((src, i) => (
                  <div className="aside-sponsor-item" key={`pr-${i}`}>
                    <a href={logoUrlMap[src] || defaultLogoUrl} target="_blank" rel="noopener noreferrer">
                      <img src={src} alt={`Partner ${i + 1}`} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="aside-column">
            <div className="aside-section">
              <div className="aside-section-title">Contributors</div>
              <div className="aside-logos-list">
                {contributorLogos.map((src, i) => (
                  <div className="aside-sponsor-item" key={`c-${i}`}>
                    <a href={logoUrlMap[src] || defaultLogoUrl} target="_blank" rel="noopener noreferrer">
                      <img src={src} alt={`Contributor ${i + 1}`} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <footer className="site-footer">
        <div className="footer-inner">
          <div className="footer-contact">
            <h4>Contact information</h4>
            <address className="footer-address">
              <div className="footer-company">startupticker.ch</div>
              <div className="footer-street">Habsburgerstrasse 31</div>
              <div className="footer-city">CH-6003 Luzern</div>
              <a href="mailto:news@startupticker.ch" className="footer-email">
                news@startupticker.ch
              </a>
              <a href="https://startupticker.ch/en/contact" className="footer-contact-link">
                Contact us
              </a>
            </address>
          </div>
        </div>
      </footer>
    </>
  );
}


export function App() {
  return (
    <DataProvider>
      <AppContent />
    </DataProvider>
  );
}

export default App;
